import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthStage, cookieStorage } from '.';
import { Method, queryData } from '../../../utilities/fetch';

interface Params {
  mfaEnabled: boolean;
  username: string;
  password: string;
  user: CognitoUser;
  appClient: string;
  apiUrl: string;
  uniCode: string;
  setAuthStage: (stage: AuthStage) => void;
  setPassword: (password: string) => void;
  setIsLoading: (loading: boolean) => void;
}

export default async function onLoginClick(params: Params): Promise<CognitoUserSession | undefined> {
  const { uniCode, mfaEnabled, username, password, user, setAuthStage, setPassword, setIsLoading, appClient, apiUrl } =
    params;

  const onSuccessLogin = (result: CognitoUserSession, resolve: any) => {
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app

    queryData({
      uri: `/users/whoami`,
      idToken: result.getIdToken().getJwtToken(),
      domain: apiUrl,
      method: Method.Post,
      body: { clientId: appClient, token: result.getRefreshToken().getToken(), username }
    }).then(() => {
      if (!mfaEnabled) {
        return resolve(result);
      }
      user.getUserData((err, userData) => {
        if (err) {
          return resolve(result);
        } else {
          const mfaEnabled = userData!.UserMFASettingList && userData!.UserMFASettingList.length > 0;
          if (mfaEnabled) {
            return resolve(result);
          } else {
            const isMfaSetupSkipped = sessionStorage.getItem('mfa.setupSkipped') === 'true';
            if (!isMfaSetupSkipped) {
              setAuthStage(AuthStage.MFA_SETUP);
            }
            return resolve(result);
          }
        }
      });
    });
  };

  const newPasswordRequired = () => {
    setAuthStage(AuthStage.NEW_PASSWORD);
    setPassword('');
    setIsLoading(false);
    return undefined;
  };

  const mfaRequired = () => {
    setAuthStage(AuthStage.SMS_CODE);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const totpRequired = () => {
    setAuthStage(AuthStage.TOTP_CODE);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const customChallenge = () => {
    return undefined;
  };

  const mfaSetup = () => {
    setAuthStage(AuthStage.MFA_SETUP);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const selectMFAType = () => {
    setAuthStage(AuthStage.MFA_SETUP);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password
  });

  const authenticateUserAsync = (
    user: CognitoUser,
    authenticationDetails: AuthenticationDetails
  ): Promise<CognitoUserSession | undefined> => {
    return new Promise((resolve, reject) => {
      user.authenticateUser(authenticationDetails, {
        onSuccess: (result: CognitoUserSession) => {
          onSuccessLogin(result, resolve);
        },
        onFailure: reason => {
          setIsLoading(false);
          // user.authenticateUser() fails when a user has an apostrophe in their username. The authentication API
          // request is successful and the session data is updated but the Device API request made after fails
          if (reason?.code === 'InvalidParameterException' && username.includes("'")) {
            const session = user.getSignInUserSession();
            onSuccessLogin(session, resolve);
          } else {
            reject(reason);
          }
        },
        newPasswordRequired: () => {
          // console.log('on newPasswordRequired!', userAttr);
          resolve(newPasswordRequired());
        },
        mfaRequired: () => {
          // console.log('on mfaRequired!');
          resolve(mfaRequired());
        },
        totpRequired: () => {
          // console.log('on totpRequired!');
          resolve(totpRequired());
        },
        customChallenge: () => {
          // console.log('on customChallenge!');
          resolve(customChallenge());
        },
        mfaSetup: () => {
          // console.log('on mfaSetup!');
          resolve(mfaSetup());
        },
        selectMFAType: () => {
          // console.log('on selectMFAType!');
          resolve(selectMFAType());
        }
      });
    });
  };

  return authenticateUserAsync(user, authenticationDetails);
}
